/**
 * CDN 站点详情 - 侧边导航菜单
 *
 * 通过 menuContentRender 挂载，只替换 ProLayout sider 的菜单内容区，
 * 保留 sider 的 CSS 结构，风格与系统其他页面完全一致。
 */
import {
  ApiOutlined,
  ArrowLeftOutlined,
  CloudOutlined,
  CompressOutlined,
  DashboardOutlined,
  DeploymentUnitOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  FireOutlined,
  GlobalOutlined,
  LinkOutlined,
  LockOutlined,
  MenuOutlined,
  RetweetOutlined,
  RobotOutlined,
  SafetyOutlined,
  ShareAltOutlined,
  ShopOutlined,
  TranslationOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Menu } from 'antd';
import React from 'react';
import { history, useIntl, useParams } from 'umi';
import type { ApiMode } from '../../types';

interface SiteSideMenuProps {
  mode: ApiMode;
}

const SiteSideMenu: React.FC<SiteSideMenuProps> = ({ mode }) => {
  const intl = useIntl();
  const { siteId, feature } = useParams<{ siteId: string; feature?: string }>();

  const t = (id: string, msg: string) =>
    intl.formatMessage({ id, defaultMessage: msg });

  const activeTab = feature || 'domains';
  const listPath = mode === 'ops' ? '/cdn-ops/sites' : '/cdn-oper/sites';
  const siteBasePath = `${listPath}/${siteId}`;

  const menuGroups = [
    ...(mode === 'oper'
      ? [
          {
            type: 'group' as const,
            label: t('cdn.site.sideMenu.group.operations', '运营管理'),
            children: [
              {
                key: 'subscription',
                icon: <ShopOutlined />,
                label: t('cdn.site.tab.subscription', '订阅管理'),
              },
            ],
          },
        ]
      : []),
    {
      type: 'group' as const,
      label: t('cdn.site.sideMenu.group.basic', '站点配置'),
      children: [
        {
          key: 'domains',
          icon: <GlobalOutlined />,
          label: t('cdn.site.tab.domains', '域名管理'),
        },
        {
          key: 'upstream',
          icon: <ApiOutlined />,
          label: t('cdn.site.tab.upstream', '回源配置'),
        },
        {
          key: 'lines',
          icon: <DeploymentUnitOutlined />,
          label: t('cdn.site.tab.lines', '线路管理'),
        },
        {
          key: 'https',
          icon: <LockOutlined />,
          label: t('cdn.site.tab.https', 'HTTPS 证书'),
        },
      ],
    },
    {
      type: 'group' as const,
      label: t('cdn.site.sideMenu.group.performance', '缓存优化'),
      children: [
        {
          key: 'cdn',
          icon: <CloudOutlined />,
          label: t('cdn.site.tab.cdn', '缓存规则'),
        },
        {
          key: 'gzip',
          icon: <CompressOutlined />,
          label: t('cdn.site.tab.gzip', '内容压缩'),
        },
        {
          key: 'bandwidth-limit',
          icon: <DashboardOutlined />,
          label: t('cdn.site.tab.bandwidthLimit', '带宽限速'),
        },
      ],
    },
    {
      type: 'group' as const,
      label: t('cdn.site.sideMenu.group.security', '安全防护'),
      children: [
        {
          key: 'cc',
          icon: <FireOutlined />,
          label: t('cdn.site.tab.cc', 'CC 防护'),
        },
        {
          key: 'waf',
          icon: <SafetyOutlined />,
          label: t('cdn.site.tab.waf', 'WAF 防护'),
        },
        {
          key: 'human',
          icon: <RobotOutlined />,
          label: t('cdn.site.tab.human', 'Bot 管理'),
        },
        {
          key: 'referer',
          icon: <LinkOutlined />,
          label: t('cdn.site.tab.referer', '防盗链'),
        },
        {
          key: 'url-auth',
          icon: <LockOutlined />,
          label: t('cdn.site.tab.urlAuth', 'URL 鉴权'),
        },
      ],
    },
    {
      type: 'group' as const,
      label: t('cdn.site.sideMenu.group.access', '访问限制'),
      children: [
        {
          key: 'ip-access',
          icon: <FilterOutlined />,
          label: t('cdn.site.tab.ipAccess', 'IP 黑白名单'),
        },
        {
          key: 'url-access',
          icon: <SafetyOutlined />,
          label: t('cdn.site.tab.urlAccess', 'URL 过滤'),
        },
        {
          key: 'geo',
          icon: <EnvironmentOutlined />,
          label: t('cdn.site.tab.geo', '地域限制'),
        },
        {
          key: 'language',
          icon: <TranslationOutlined />,
          label: t('cdn.site.tab.language', '语言限制'),
        },
        {
          key: 'user-agents',
          icon: <UserOutlined />,
          label: t('cdn.site.tab.ua', 'UA 过滤'),
        },
      ],
    },
    {
      type: 'group' as const,
      label: t('cdn.site.sideMenu.group.advanced', '高级功能'),
      children: [
        {
          key: 'cors',
          icon: <ShareAltOutlined />,
          label: t('cdn.site.tab.cors', 'CORS'),
        },
        {
          key: 'headers',
          icon: <MenuOutlined />,
          label: t('cdn.site.tab.headers', '请求头管理'),
        },
        {
          key: 'url-rewrites',
          icon: <RetweetOutlined />,
          label: t('cdn.site.tab.urlRewrite', 'URL 重写'),
        },
      ],
    },
  ];

  return (
    <Menu
      mode="inline"
      selectedKeys={[activeTab]}
      style={{ borderRight: 0 }}
      onClick={({ key }) => {
        if (key === '__back__') {
          history.push(listPath);
        } else {
          history.push(`${siteBasePath}/${key}`);
        }
      }}
      items={[
        {
          key: '__back__',
          icon: <ArrowLeftOutlined />,
          label: t('cdn.site.sideMenu.backToList', '返回站点列表'),
        },
        { type: 'divider' },
        ...menuGroups,
      ]}
    />
  );
};

export default SiteSideMenu;
